import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  Text,
  Banner,
  SkeletonBodyText,
  SkeletonDisplayText,
  Box,
  InlineStack,
  Badge,
} from '@shopify/polaris';
import {
  useLatestSimulation,
  useRunSimulation,
} from '../hooks/useCrawlSimulation';
import { PageEntrance } from '../components/ui';
import { SEMANTIC_COLORS } from '../theme';
import type { SimulationReport } from '../types';
import './CrawlSimulationPage.css';

/* ── Sub-Components ──────────────────────────────────────────── */

/** Hero CTA with single "Run Crawl Simulation" button */
function HeroCTA({
  onRun,
  isPending,
  errorMessage,
}: Readonly<{
  onRun: () => void;
  isPending: boolean;
  errorMessage?: string;
}>) {
  return (
    <div className="xray-hero">
      <div className="xray-hero-content">
        <h2 className="xray-hero-title">
          See your product through the eyes of AI
        </h2>
        <p className="xray-hero-subtitle">
          Discover exactly what ChatGPT, Perplexity, Google Gemini and AI shopping
          assistants can — and can't — extract from your product page.
        </p>
        <div className="xray-hero-buttons">
          <button
            type="button"
            className="xray-btn-primary"
            onClick={onRun}
            disabled={isPending}
          >
            {isPending ? '⏳ Scanning…' : '🔬 Run Crawl Simulation'}
          </button>
        </div>
        {errorMessage && (
          <div className="xray-error-wrapper">
            <Banner tone="critical">{errorMessage}</Banner>
          </div>
        )}
      </div>
    </div>
  );
}


/** Scanning animation */
function ScanningIndicator() {
  return (
    <div className="xray-scanning-indicator">
      <div className="xray-scanning-dots">
        <div className="xray-scanning-dot" />
        <div className="xray-scanning-dot" />
        <div className="xray-scanning-dot" />
      </div>
      <span className="xray-scanning-text">
        Scanning your product page… Crawling structured data, meta tags, and schema markup.
      </span>
    </div>
  );
}

/* ── Report Panel Components (Phase 3.1) ─────────────────── */

function QualityChip({ quality }: Readonly<{ quality: 'present' | 'partial' | 'missing' }>) {
  const colorMap = {
    present: { color: SEMANTIC_COLORS.good, bg: `${SEMANTIC_COLORS.good}22`, border: `${SEMANTIC_COLORS.good}55` },
    partial: { color: SEMANTIC_COLORS.warning, bg: `${SEMANTIC_COLORS.warning}22`, border: `${SEMANTIC_COLORS.warning}55` },
    missing: { color: SEMANTIC_COLORS.critical, bg: `${SEMANTIC_COLORS.critical}22`, border: `${SEMANTIC_COLORS.critical}55` },
  };
  const labelMap = { present: 'Present', partial: 'Partial', missing: 'Missing' };
  const c = colorMap[quality];
  return (
    <span
      className="sim-quality-chip"
      {...{ style: { '--sim-chip-color': c.color, '--sim-chip-bg': c.bg, '--sim-chip-border': c.border } as React.CSSProperties }}
    >
      {labelMap[quality]}
    </span>
  );
}

function getConfidenceColor(value: number): string {
  if (value >= 70) return SEMANTIC_COLORS.good;
  if (value >= 40) return SEMANTIC_COLORS.warning;
  return SEMANTIC_COLORS.critical;
}

function ConfidenceGauge({ value }: Readonly<{ value: number }>) {
  const color = getConfidenceColor(value);
  return (
    <div
      className="sim-confidence"
      {...{ style: { '--sim-confidence-pct': `${Math.min(100, value)}%`, '--sim-confidence-color': color } as React.CSSProperties }}
    >
      <div className="sim-confidence-track">
        <div className="sim-confidence-fill" />
      </div>
      <span className="sim-confidence-value">{value}</span>
    </div>
  );
}

/** Panel 1 — AI Crawler View */
function CrawlerViewPanel({ report }: Readonly<{ report: SimulationReport }>) {
  const [open, setOpen] = useState(true);
  const view = report.crawlerView;
  if (!view) return null;

  return (
    <Card padding="0">
      <div className="xray-category">
        <button
          type="button"
          className="xray-category-header xray-category-header-btn"
          onClick={() => setOpen(!open)}
          {...{ 'aria-expanded': open }}
        >
          <div className="xray-category-left">
            <span className="xray-category-icon">🤖</span>
            <span className="xray-category-title">AI Crawler View</span>
          </div>
          <div className="xray-category-right">
            <span className="xray-category-chevron" data-open={open ? 'true' : 'false'}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </div>
        </button>
        {open && (
          <div className="sim-panel-body">
            <BlockStack gap="400">
              <Text as="p" variant="bodySm" tone="subdued">{view.whatICrawled}</Text>

              {/* Crawler confidence */}
              <div>
                <Text as="p" variant="bodySm" fontWeight="semibold">Crawler Confidence Score</Text>
                <Box paddingBlockStart="200">
                  <ConfidenceGauge value={view.crawlerConfidence} />
                </Box>
              </div>

              {/* Structured data quality */}
              {view.structuredDataFound.length > 0 && (
                <div>
                  <Text as="p" variant="bodySm" fontWeight="semibold">Structured Data Found</Text>
                  <BlockStack gap="200">
                    {view.structuredDataFound.map((item) => (
                      <div key={item.type} className="sim-schema-row">
                        <QualityChip quality={item.quality} />
                        <div>
                          <Text as="p" variant="bodySm" fontWeight="semibold">{item.type}</Text>
                          <Text as="p" variant="bodySm" tone="subdued">{item.detail}</Text>
                        </div>
                      </div>
                    ))}
                  </BlockStack>
                </div>
              )}

            </BlockStack>
          </div>
        )}
      </div>
    </Card>
  );
}

/** Panel 2 — AI Query Simulation */
function QuerySimulationPanel({ report }: Readonly<{ report: SimulationReport }>) {
  const [open, setOpen] = useState(true);
  const sim = report.aiQuerySimulation;
  if (!sim?.queries.length) return null;

  return (
    <Card padding="0">
      <div className="xray-category">
        <button
          type="button"
          className="xray-category-header xray-category-header-btn"
          onClick={() => setOpen(!open)}
          {...{ 'aria-expanded': open }}
        >
          <div className="xray-category-left">
            <span className="xray-category-icon">💬</span>
            <span className="xray-category-title">AI Query Simulation</span>
          </div>
          <div className="xray-category-right">
            <span className="xray-category-chevron" data-open={open ? 'true' : 'false'}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </div>
        </button>
        {open && (
          <div className="sim-panel-body">
            <BlockStack gap="500">
              {sim.queries.map((q) => {
                const borderColor = q.canAnswerFromPage ? SEMANTIC_COLORS.good : SEMANTIC_COLORS.critical;
                return (
                  <div
                    key={q.query}
                    className="sim-query-card"
                    {...{ style: { '--sim-query-border': borderColor } as React.CSSProperties }}
                  >
                    <BlockStack gap="300">
                      <div className="sim-query-header">
                        <Text as="p" variant="bodySm" fontWeight="semibold">"{q.query}"</Text>
                        <Badge tone={q.canAnswerFromPage ? 'success' : 'critical'}>
                          {q.canAnswerFromPage ? 'Can answer' : 'Cannot answer'}
                        </Badge>
                      </div>

                      <div className="sim-confidence-grid">
                        <div>
                          <Text as="p" variant="bodySm" tone="subdued">Confidence now</Text>
                          <ConfidenceGauge value={q.confidenceIfRecommended} />
                        </div>
                        <div>
                          <Text as="p" variant="bodySm" tone="subdued">After fixes</Text>
                          <ConfidenceGauge value={q.confidenceAfterFixes} />
                        </div>
                      </div>

                      {!q.canAnswerFromPage && (
                        <Text as="p" variant="bodySm" tone="subdued">{q.whyCannotAnswer}</Text>
                      )}

                      <div className="sim-fix-hint">
                        <Text as="p" variant="bodySm" tone="subdued">
                          <strong className="sim-fix-hint-label">Fix: </strong>{q.whatWouldFixIt}
                        </Text>
                      </div>

                      {q.benefitingChannels.length > 0 && (
                        <InlineStack gap="100" wrap>
                          {q.benefitingChannels.map((ch) => (
                            <Badge key={ch} tone="info">{ch}</Badge>
                          ))}
                        </InlineStack>
                      )}
                    </BlockStack>
                  </div>
                );
              })}
            </BlockStack>
          </div>
        )}
      </div>
    </Card>
  );
}

type ReadinessLabel = 'Not Ready' | 'Needs Work' | 'Almost Ready' | 'Ready';

function readinessTone(label: ReadinessLabel): 'critical' | 'warning' | 'attention' | 'success' {
  if (label === 'Not Ready') return 'critical';
  if (label === 'Needs Work') return 'warning';
  if (label === 'Almost Ready') return 'attention';
  return 'success';
}

function getEffortImpactColor(level: 'low' | 'medium' | 'high'): string {
  if (level === 'low') return SEMANTIC_COLORS.good;
  if (level === 'high') return SEMANTIC_COLORS.critical;
  return SEMANTIC_COLORS.warning;
}

/** Panel 3 — Channel Readiness + Revenue Impact */
function ChannelReadinessPanel({ report }: Readonly<{ report: SimulationReport }>) {
  const [open, setOpen] = useState(true);
  const channels = report.channelReadiness;
  const revenue = report.revenueImpact;
  const visibility = report.aiVisibilityScore;
  if (!channels?.length && !revenue) return null;

  return (
    <Card padding="0">
      <div className="xray-category">
        <button
          type="button"
          className="xray-category-header xray-category-header-btn"
          onClick={() => setOpen(!open)}
          {...{ 'aria-expanded': open }}
        >
          <div className="xray-category-left">
            <span className="xray-category-icon">📊</span>
            <span className="xray-category-title">Channel Readiness &amp; Revenue Impact</span>
          </div>
          <div className="xray-category-right">
            {visibility && (
              <span className="sim-grade-change">{visibility.tierChange}</span>
            )}
            <span className="xray-category-chevron" data-open={open ? 'true' : 'false'}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </div>
        </button>
        {open && (
          <div className="sim-panel-body">
            <BlockStack gap="500">

              {/* Channel grid */}
              {channels && channels.length > 0 && (
                <div>
                  <Text as="p" variant="bodySm" fontWeight="semibold">AI Shopping Channel Eligibility</Text>
                  <div className="sim-channel-grid">
                    {channels.map((ch) => (
                      <div key={ch.channel} className="sim-channel-card">
                        <div className="sim-channel-header">
                          <Text as="p" variant="bodySm" fontWeight="semibold">{ch.channel}</Text>
                          <Badge tone={readinessTone(ch.label as ReadinessLabel)}>{ch.label}</Badge>
                        </div>
                        <ConfidenceGauge value={ch.score} />
                        <Text as="p" variant="bodySm" tone="subdued">{ch.topBlocker}</Text>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* AI Visibility Score */}
              {visibility && (
                <div className="sim-visibility-strip">
                  <div>
                    <Text as="p" variant="bodySm" tone="subdued">AI Visibility Score (now)</Text>
                    <Text as="p" variant="headingLg">{visibility.current}/100</Text>
                  </div>
                  <div>
                    <Text as="p" variant="bodySm" tone="subdued">After fixes</Text>
                    <Text as="p" variant="headingLg">{visibility.projected}/100</Text>
                  </div>
                  <div>
                    <Text as="p" variant="bodySm" tone="subdued">Tier change</Text>
                    <Text as="p" variant="headingMd" fontWeight="bold">{visibility.tierChange}</Text>
                  </div>
                </div>
              )}

              {/* Revenue impact */}
              {revenue && (
                <div>
                  <Text as="p" variant="bodySm" fontWeight="semibold">Revenue Impact Estimates</Text>
                  <BlockStack gap="300">
                    {revenue.topFixes.map((fix) => {
                      const effortColor = getEffortImpactColor(fix.effort);
                      const impactColor = getEffortImpactColor(fix.impact);
                      return (
                        <div key={fix.fix} className="sim-fix-card">
                          <BlockStack gap="200">
                            <div className="sim-fix-card-header">
                              <Text as="p" variant="bodySm" fontWeight="semibold">{fix.fix}</Text>
                              <InlineStack gap="100">
                                <span
                                  className="sim-effort-badge"
                                  {...{ style: { '--sim-badge-color': effortColor, '--sim-badge-border': `${effortColor}55` } as React.CSSProperties }}
                                >
                                  {fix.effort} effort
                                </span>
                                <span
                                  className="sim-effort-badge"
                                  {...{ style: { '--sim-badge-color': impactColor, '--sim-badge-border': `${impactColor}55` } as React.CSSProperties }}
                                >
                                  {fix.impact} impact
                                </span>
                              </InlineStack>
                            </div>
                            <Text as="p" variant="bodySm" tone="subdued">{fix.rationale}</Text>
                            <Text as="p" variant="bodySm" fontWeight="semibold" tone="success">
                              Est. uplift: {fix.estimatedMonthlyUplift}/mo
                            </Text>
                          </BlockStack>
                        </div>
                      );
                    })}
                  </BlockStack>

                  {(revenue.totalProductEstimate || revenue.catalogScaleNote) && (
                    <div className="sim-revenue-total">
                      {revenue.totalProductEstimate && (
                        <Text as="p" variant="bodySm" fontWeight="semibold">
                          Total estimate for this product: {revenue.totalProductEstimate}
                        </Text>
                      )}
                      {revenue.catalogScaleNote && (
                        <Text as="p" variant="bodySm" tone="subdued">{revenue.catalogScaleNote}</Text>
                      )}
                    </div>
                  )}
                </div>
              )}

            </BlockStack>
          </div>
        )}
      </div>
    </Card>
  );
}

/* ── Main Page Component ─────────────────────────────────────── */

/**
 * AI Visibility X-Ray Page
 *
 * Redesigned from flat "AI Crawl Simulation" into a dramatic,
 * value-selling experience. Shows merchants exactly what AI engines
 * see — grouped by category with progress indicators, smart insights,
 * and a clear before→after score projection.
 */
export function CrawlSimulationPage(): React.ReactElement {
  const { id: productId } = useParams<{ id: string }>();
  const {
    data: simulation,
    isLoading: isLoadingSimulation,
  } = useLatestSimulation(productId ?? '');
  const runSimulation = useRunSimulation();

  // Derive result from mutation data or latest stored simulation
  const result = runSimulation.data ?? (simulation ? {
    simulation,
    gapCount: simulation.gaps.filter(
      (g) => g.status === 'missing' || g.status === 'incomplete',
    ).length,
    presentCount: simulation.gaps.filter((g) => g.status === 'present').length,
    totalFields: simulation.gaps.length,
  } : null);

  if (!productId) {
    return (
      <Page title="Crawl Simulation">
        <Banner tone="critical">No product ID provided.</Banner>
      </Page>
    );
  }

  const handleRunSimulation = () => {
    runSimulation.mutate({ productId });
  };

  return (
    <PageEntrance>
      <Page
        title="Crawl Simulation"
        subtitle="Discover how AI engines interpret your product data"
        backAction={{ content: 'Product', url: `/products/${productId}` }}
      >
        <Layout>
          {/* Hero CTA Section */}
          <Layout.Section>
            <HeroCTA
              onRun={handleRunSimulation}
              isPending={runSimulation.isPending}
              errorMessage={
                runSimulation.isError ? runSimulation.error.message : undefined
              }
            />
          </Layout.Section>

          {/* Loading state */}
          {(isLoadingSimulation || runSimulation.isPending) && (
            <Layout.Section>
              {runSimulation.isPending ? (
                <ScanningIndicator />
              ) : (
                <Card>
                  <SkeletonDisplayText size="small" />
                  <SkeletonBodyText lines={6} />
                </Card>
              )}
            </Layout.Section>
          )}

          {/* Results */}
          {result && !runSimulation.isPending && (
            <>
              {/* Password protection warning from simulationReport (live page unavailable) */}
              {result.simulation.simulationReport?.passwordProtectedWarning && (
                <Layout.Section>
                  <Banner tone="warning">
                    <p>
                      <strong>Your storefront is password-protected.</strong> This simulation
                      used your product data instead of the live page HTML — structured data
                      accuracy may be lower than a live-store result.
                    </p>
                  </Banner>
                </Layout.Section>
              )}

              {/* Legacy password-protected warning (store fully inaccessible) */}
              {result.simulation.isPasswordProtected && !result.simulation.simulationReport?.passwordProtectedWarning && (
                <Layout.Section>
                  <Banner tone="warning">
                    <p>
                      <strong>Your store is password-protected.</strong> AI crawlers
                      and shopping assistants cannot access your product pages. Remove
                      the password to unlock AI discoverability.
                    </p>
                  </Banner>
                </Layout.Section>
              )}

              {/* LLM Report Panels — rendered when simulationReport is present */}
              {result.simulation.simulationReport && (
                <>
                  <Layout.Section>
                    <Text as="h2" variant="headingMd">
                      AI Simulation Report
                    </Text>
                  </Layout.Section>
                  <Layout.Section>
                    <CrawlerViewPanel report={result.simulation.simulationReport} />
                  </Layout.Section>
                  <Layout.Section>
                    <QuerySimulationPanel report={result.simulation.simulationReport} />
                  </Layout.Section>
                  <Layout.Section>
                    <ChannelReadinessPanel report={result.simulation.simulationReport} />
                  </Layout.Section>
                </>
              )}

            </>
          )}

          {/* Empty state — no results yet */}
          {!result && !isLoadingSimulation && !runSimulation.isPending && (
            <Layout.Section>
              <Card>
                <div className="xray-empty">
                  <span className="xray-empty-icon">🔍</span>
                  <span className="xray-empty-title">
                    No simulation yet
                  </span>
                  <span className="xray-empty-desc">
                    Run a Crawl Simulation to see exactly what AI shopping assistants,
                    search engines, and language models can extract from your
                    product — and what they're missing.
                  </span>
                </div>
              </Card>
            </Layout.Section>
          )}

          <Layout.Section>
            <Box paddingBlockEnd="800" />
          </Layout.Section>
        </Layout>
      </Page>
    </PageEntrance>
  );
}
